import React, { useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Headphones, Play, ExternalLink, Calendar, User, Mic, Pause } from 'lucide-react';
import usePageMeta from '@/hooks/usePageMeta';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';

const BACKEND_URL = '';
const BRITECAST_BANNER_IMAGE = '/britecast-hero-v2.jpg';

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function EpisodeCard({ ep, currentId, isPlaying, onTogglePlay }) {
  const isCurrent = currentId === ep.episode_id;
  return (
    <Card className="overflow-hidden border border-slate-200 hover:border-brand-navy/30 hover:shadow-md transition-all"
      data-testid={`britecast-episode-${ep.episode_id}`}>
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {ep.episode_number ? (
              <p className="text-[10px] uppercase tracking-wide font-bold text-brand-sage mb-1">
                Episode {ep.episode_number}
              </p>
            ) : null}
            <h3 className="text-base font-bold text-slate-900 leading-snug">{ep.title}</h3>
            {ep.guest && (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                <User className="h-3 w-3" /> With {ep.guest}
              </p>
            )}
          </div>
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-brand-navy to-brand-sage flex items-center justify-center text-white shadow-md">
            <Mic className="h-5 w-5" />
          </div>
        </div>

        {ep.description && (
          <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{ep.description}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {ep.tags?.map(tag => (
            <Badge key={tag} variant="outline" className="text-[10px] bg-slate-50">{tag}</Badge>
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t border-slate-100">
          <div className="flex items-center gap-3">
            {ep.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(ep.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
            {ep.duration_seconds ? <span>{formatDuration(ep.duration_seconds)}</span> : null}
            {!ep.duration_seconds && ep.audio_size ? <span>{formatBytes(ep.audio_size)}</span> : null}
          </div>
          {ep.external_url && !ep.has_audio_file && (
            <a href={ep.external_url} target="_blank" rel="noopener noreferrer"
              data-testid={`britecast-external-${ep.episode_id}`}
              className="text-brand-navy hover:underline flex items-center gap-1">
              Listen externally <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        {(ep.has_audio_file || (ep.audio_url || ep.external_url)) && (
          <button
            type="button"
            onClick={() => onTogglePlay(ep)}
            data-testid={`britecast-play-${ep.episode_id}`}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-semibold text-sm transition-colors ${
              isCurrent && isPlaying
                ? 'bg-brand-navy text-white hover:bg-brand-dark'
                : 'bg-brand-sage text-white hover:bg-brand-sage/90'
            }`}
          >
            {isCurrent && isPlaying ? (<><Pause className="h-4 w-4" /> Pause</>) : (<><Play className="h-4 w-4" /> Play episode</>)}
          </button>
        )}
      </div>
    </Card>
  );
}

export default function BriteCastPage({ user }) {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentEp, setCurrentEp] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = React.useRef(null);

  usePageMeta({ title: 'BriteCast', description: 'Podcasts from the SLL community — listen to industry voices on sustainability, logistics and supply chain innovation.' });

  useEffect(() => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/britecast`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setEpisodes)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleTogglePlay = (ep) => {
    if (currentEp?.episode_id === ep.episode_id) {
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      return;
    }
    setCurrentEp(ep);
    setIsPlaying(true);
    setTimeout(() => audioRef.current?.play(), 50);
  };

  const audioSrc = currentEp
    ? (currentEp.has_audio_file ? `${BACKEND_URL}/api/britecast/${currentEp.episode_id}/audio` : (currentEp.external_url || currentEp.audio_url))
    : '';

  // Detect embeddable video providers — an HTML <audio> element cannot
  // play a Vimeo/YouTube page URL, only raw audio streams. Fall back to
  // an <iframe> embed so members can listen (and watch) in-page.
  const getEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, '');
      // Vimeo: https://vimeo.com/{id}  or  https://vimeo.com/{id}/{hash}
      if (host === 'vimeo.com' || host === 'player.vimeo.com') {
        const parts = u.pathname.split('/').filter(Boolean);
        const id = parts[0];
        const hash = parts[1];
        if (/^\d+$/.test(id)) {
          const base = `https://player.vimeo.com/video/${id}`;
          return hash ? `${base}?h=${hash}` : base;
        }
      }
      // YouTube: https://youtu.be/{id}  or  https://youtube.com/watch?v={id}
      if (host === 'youtu.be') {
        const id = u.pathname.split('/').filter(Boolean)[0];
        if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
      }
      if (host === 'youtube.com' || host === 'm.youtube.com') {
        const id = u.searchParams.get('v');
        if (id) return `https://www.youtube-nocookie.com/embed/${id}`;
      }
    } catch { /* fall through */ }
    return null;
  };
  const embedUrl = currentEp && !currentEp.has_audio_file ? getEmbedUrl(audioSrc) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="container mx-auto px-4 sm:px-6 py-6 md:py-10 max-w-7xl">
        <SponsorBanner placement="britecast" type="top" className="mb-6 flex justify-center" />
        <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 min-w-0">
        {/* Banner */}
        <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 mb-8 relative bg-gradient-to-br from-brand-navy via-brand-navy to-brand-sage" data-testid="britecast-banner">
          <img
            src={BRITECAST_BANNER_IMAGE}
            alt="Change Makers in Motion — a new series hearing directly from experts on enabling sustainable logistics now"
            className="w-full h-auto block"
            loading="eager"
          />
        </div>

        <div className="mb-6">
          <h1 className="text-2xl md:text-4xl font-bold font-heading gradient-text flex items-center gap-2">
            <Headphones className="h-7 w-7 text-brand-navy" /> BriteCast
          </h1>
          <p className="text-sm md:text-base text-slate-600 mt-1">
            Voices from across the sustainable logistics community — listen to interviews, panels and member stories.
          </p>
        </div>

        {loading ? (
          <Card className="p-12 text-center text-slate-500" data-testid="britecast-loading">
            Loading episodes…
          </Card>
        ) : episodes.length === 0 ? (
          <Card className="p-12 text-center" data-testid="britecast-empty">
            <Mic className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-700">No episodes yet</p>
            <p className="text-sm text-slate-500 mt-2">
              We're putting the first BriteCast episodes together. Check back soon.
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="britecast-grid">
            {episodes.map(ep => (
              <EpisodeCard
                key={ep.episode_id}
                ep={ep}
                currentId={currentEp?.episode_id}
                isPlaying={isPlaying}
                onTogglePlay={handleTogglePlay}
              />
            ))}
          </div>
        )}
        </div>
        <PageSideRail placement="britecast" />
        </div>

        {/* Sticky audio player when an episode is selected.
            - Raw audio URLs / uploaded audio files → <audio> controls.
            - Vimeo / YouTube URLs → <iframe> embed floating above the
              bar so it actually plays inside the page (audio element
              can't source a video-provider page URL). */}
        {currentEp && (
          <>
            {embedUrl && (
              <div
                className="fixed bottom-16 right-4 z-40 bg-black rounded-lg overflow-hidden shadow-2xl border border-slate-200 w-[min(90vw,480px)] aspect-video"
                data-testid="britecast-embed-frame"
              >
                <iframe
                  src={`${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=1`}
                  title={currentEp.title}
                  className="w-full h-full"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  frameBorder="0"
                />
              </div>
            )}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40 px-4 py-3"
              data-testid="britecast-player-bar">
              <div className="container mx-auto max-w-5xl flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-navy to-brand-sage flex items-center justify-center text-white flex-shrink-0">
                  <Mic className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{currentEp.title}</p>
                  {currentEp.guest && <p className="text-xs text-slate-500 truncate">With {currentEp.guest}</p>}
                </div>
                {embedUrl ? (
                  <a
                    href={currentEp.external_url || currentEp.audio_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand-navy hover:underline flex-shrink-0"
                    data-testid="britecast-open-external"
                  >
                    Open on Vimeo
                  </a>
                ) : (
                  <audio
                    ref={audioRef}
                    src={audioSrc}
                    controls
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onEnded={() => setIsPlaying(false)}
                    className="flex-shrink-0 max-w-[480px] w-full"
                    data-testid="britecast-audio-element"
                  />
                )}
                <button
                  type="button"
                  onClick={() => { audioRef.current?.pause(); setCurrentEp(null); setIsPlaying(false); }}
                  data-testid="britecast-close-player"
                  className="text-slate-400 hover:text-slate-700 text-xs font-medium flex-shrink-0 px-2 py-1"
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
